import { type ListenerEffectAPI } from '@reduxjs/toolkit'
import { AgentV2ConversationsActions, AgentV2DetailLoadError } from '@/agent-v2/conversations/store/slice'
import { AppDispatch, RootState } from '@/store/store'

// Nothing pushes chat updates while no agent run is active, so a conversation with an open export
// rereads its detail until the export's ready or failure notice arrives.
const kOpenDocxExportPollIntervalMs = 3000
const openDocxExportPollTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** Refetch the conversation soon if it still has an open export; one timer per conversation. */
export function scheduleOpenDocxExportPoll(conversationId: string, listenerApi: ListenerEffectAPI<RootState, AppDispatch>) {
  if (openDocxExportPollTimers.has(conversationId)) return
  if (!listenerApi.getState().agentV2ConversationsState.details[conversationId]?.open_docx_exports?.length) return
  const timer = setTimeout(() => {
    openDocxExportPollTimers.delete(conversationId)
    const state = listenerApi.getState().agentV2ConversationsState
    if (!state.details[conversationId]?.open_docx_exports?.length || state.deletionStatusByConversationId[conversationId]) return
    if (state.detailLoadingByConversationId[conversationId]) {
      scheduleOpenDocxExportPoll(conversationId, listenerApi)
      return
    }
    listenerApi.dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
  }, kOpenDocxExportPollIntervalMs)
  openDocxExportPollTimers.set(conversationId, timer)
}

/** After a failed reread: retry a transient failure, but stop for a chat that is gone or out of reach. */
export function handleOpenDocxExportPollError(
  conversationId: string,
  loadError: AgentV2DetailLoadError | null,
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>
) {
  if (loadError === AgentV2DetailLoadError.RETRYABLE) {
    scheduleOpenDocxExportPoll(conversationId, listenerApi)
    return
  }
  clearTimeout(openDocxExportPollTimers.get(conversationId))
  openDocxExportPollTimers.delete(conversationId)
}

export function clearOpenDocxExportPolls() {
  for (const timer of openDocxExportPollTimers.values()) clearTimeout(timer)
  openDocxExportPollTimers.clear()
}
